"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UNFUNDED_ADDITION_NOTE = void 0;
exports.milestoneIsOnInvoice = milestoneIsOnInvoice;
exports.milestoneAmountFundingVariation = milestoneAmountFundingVariation;
exports.allocateInvoiceDeductions = allocateInvoiceDeductions;
exports.invoicePositionFunding = invoicePositionFunding;
exports.annotateUnfundedInvoiceLines = annotateUnfundedInvoiceLines;
exports.fundedCasePositionDisplay = fundedCasePositionDisplay;
exports.sumFundedCaseEscrow = sumFundedCaseEscrow;
exports.casePositionEscrowDisplay = casePositionEscrowDisplay;
const round_money_js_1 = require("../money/round-money.js");
const case_payment_status_helpers_js_1 = require("./case-payment-status.helpers.js");
/** True when this milestone is billed on the invoice, including a variation-only invoice with no milestone. */
function milestoneIsOnInvoice(milestone, invoice) {
    if (!milestone || !invoice)
        return false;
    return ((invoice.projectMilestones ?? []).some((item) => item.id === milestone.id) ||
        milestone.projectInvoiceId === invoice.id ||
        milestone.invoice?.id === invoice.id);
}
/**
 * Milestone budget that shares this invoice's captured amount.
 * A variation on its own invoice has no milestone, so the captured amount funds the variation.
 */
function milestoneAmountFundingVariation(milestone, invoice) {
    if (!milestoneIsOnInvoice(milestone, invoice))
        return 0;
    return Number(milestone?.budgetAmount || 0);
}
function deductionCode(line) {
    const code = line.displayCode?.trim();
    return code || "Deduction";
}
function creationTime(value) {
    if (value == null || value === "")
        return Number.POSITIVE_INFINITY;
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
}
/** Earlier created variations are funded first. Undated lines keep the caller's order. */
function additionsInFundingOrder(additions) {
    if (!additions.some((line) => creationTime(line.createdAt) !== Number.POSITIVE_INFINITY)) {
        return additions;
    }
    return [...additions].sort((left, right) => {
        const leftTime = creationTime(left.createdAt);
        const rightTime = creationTime(right.createdAt);
        if (leftTime !== rightTime)
            return leftTime - rightTime;
        return left.id.localeCompare(right.id);
    });
}
/**
 * Deductions reduce the milestone first. Any remainder reduces variations in list order,
 * which is how a variation can keep the invoice above zero while the deduction exceeds the milestone.
 */
function allocateInvoiceDeductions(input) {
    const pools = input.deductions
        .map((line) => ({
        id: line.id,
        displayCode: deductionCode(line),
        remaining: (0, round_money_js_1.roundMoney)(Math.max(0, Number(line.amount) || 0)),
    }))
        .filter((line) => line.remaining > 0);
    const take = (cap) => {
        let room = (0, round_money_js_1.roundMoney)(Math.max(0, cap));
        const lines = [];
        for (const pool of pools) {
            if (room <= 0 || pool.remaining <= 0)
                continue;
            const used = (0, round_money_js_1.roundMoney)(Math.min(room, pool.remaining));
            if (used <= 0)
                continue;
            pool.remaining = (0, round_money_js_1.roundMoney)(pool.remaining - used);
            room = (0, round_money_js_1.roundMoney)(room - used);
            lines.push({ id: pool.id, displayCode: pool.displayCode, amount: used });
        }
        return { amount: (0, round_money_js_1.roundMoney)(lines.reduce((sum, line) => sum + line.amount, 0)), lines };
    };
    const additions = additionsInFundingOrder(input.additions);
    const milestone = take(input.milestoneAmount);
    return {
        milestone: { deduction: milestone.amount, lines: milestone.lines },
        additions: additions.map((addition) => {
            const taken = take(addition.amount);
            return { id: addition.id, deduction: taken.amount, lines: taken.lines };
        }),
    };
}
/**
 * Captured escrow funds the milestone first, then variations in creation order.
 * A variation added after the invoice was paid is not funded until a new payment covers it.
 */
function invoicePositionFunding(input) {
    const additionsInOrder = additionsInFundingOrder(input.additions);
    const allocation = allocateInvoiceDeductions({ ...input, additions: additionsInOrder });
    const milestoneNet = (0, round_money_js_1.roundMoney)(Math.max(0, Number(input.milestoneAmount) - allocation.milestone.deduction));
    // A missing captured amount is a legacy invoice that funded the milestone.
    // An explicit zero is a real capture, including a discount that covered the invoice.
    const capturedIsStored = input.captured != null && Number.isFinite(Number(input.captured));
    const stored = capturedIsStored ? (0, round_money_js_1.roundMoney)(Math.max(0, Number(input.captured))) : null;
    let pool = stored != null ? stored : milestoneNet;
    const milestoneFunded = (0, round_money_js_1.roundMoney)(Math.min(pool, milestoneNet));
    pool = (0, round_money_js_1.roundMoney)(Math.max(0, pool - milestoneFunded));
    const additions = additionsInOrder.map((addition) => {
        const row = allocation.additions.find((item) => item.id === addition.id);
        const net = (0, round_money_js_1.roundMoney)(Math.max(0, Number(addition.amount) - (row?.deduction ?? 0)));
        const funded = (0, round_money_js_1.roundMoney)(Math.min(pool, net));
        pool = (0, round_money_js_1.roundMoney)(Math.max(0, pool - funded));
        return { id: addition.id, net, funded, lines: row?.lines ?? [] };
    });
    return {
        milestone: { net: milestoneNet, funded: milestoneFunded, lines: allocation.milestone.lines },
        additions,
    };
}
exports.UNFUNDED_ADDITION_NOTE = "Not included in this payment";
/** Marks additions a paid invoice did not capture, so the line list can explain why they are absent from the total. */
function annotateUnfundedInvoiceLines(lines, input) {
    if (!input.paid) {
        return lines.map((line) => ({ ...line, excludedFromPayment: false }));
    }
    const funding = invoicePositionFunding({
        captured: input.captured,
        milestoneAmount: input.milestoneAmount,
        additions: lines.filter((line) => line.amount > 0).map((line) => ({ id: line.id, amount: line.amount, createdAt: line.createdAt })),
        deductions: lines.filter((line) => line.amount < 0).map((line) => ({ id: line.id, amount: Math.abs(line.amount) })),
    });
    return lines.map((line) => {
        const funded = funding.additions.find((row) => row.id === line.id)?.funded ?? 0;
        return { ...line, excludedFromPayment: line.amount > 0 && funded <= 0 };
    });
}
function variationPosition(funding, additions, target) {
    const row = funding.additions.find((item) => item.id === target.id);
    const gross = additions.find((item) => item.id === target.id)?.amount ?? 0;
    if (!row)
        return null;
    return {
        gross,
        net: row.net,
        funded: row.funded,
        lines: row.lines,
        drawdown: undefined,
        fullyReleased: target.fullyReleased,
        subject: "variation",
    };
}
/** Escrow columns for one position, limited to the slice of captured that position can actually claim. */
function fundedCasePositionDisplay(input) {
    const funding = invoicePositionFunding(input);
    const target = input.target;
    const position = target.kind === "milestone"
        ? { gross: input.milestoneAmount, net: funding.milestone.net, funded: funding.milestone.funded, lines: funding.milestone.lines, drawdown: target.drawdownAmount, fullyReleased: target.fullyReleased, subject: "milestone" }
        : variationPosition(funding, input.additions, target);
    if (!position)
        return null;
    const unfundedGap = position.funded + 1e-9 < position.net;
    return casePositionEscrowDisplay({
        positionAmount: unfundedGap ? position.funded : position.gross,
        allocatedDeductions: unfundedGap ? [] : position.lines,
        fullyReleased: position.fullyReleased,
        drawdownAmount: position.drawdown,
        releasePercent: input.releasePercent,
        paid: input.paid,
        subject: position.subject,
    });
}
/** Header totals: funded milestone and variation slices. A shared invoice keeps one split of its captured amount. */
function sumFundedCaseEscrow(input) {
    let totalReleased = 0;
    let totalHeld = 0;
    for (const invoice of input.invoices ?? []) {
        const milestones = (input.milestones ?? []).filter((milestone) => (milestone.projectInvoiceId ?? milestone.invoice?.id) === invoice.id);
        if (invoice.instantReleasePercent == null) {
            for (const milestone of milestones) {
                const amounts = (0, case_payment_status_helpers_js_1.getCaseMilestoneEscrowAmounts)(milestone, input.invoices);
                totalReleased += amounts.released;
                totalHeld += amounts.held;
            }
            continue;
        }
        const paid = String(invoice.status || "").toLowerCase() === "paid";
        if (!paid)
            continue;
        if (milestones.length !== 1) {
            const rawCaptured = invoice.milestoneAmountCaptured;
            if (rawCaptured == null || !Number.isFinite(Number(rawCaptured))) {
                for (const milestone of milestones) {
                    const amounts = (0, case_payment_status_helpers_js_1.getCaseMilestoneEscrowAmounts)(milestone, input.invoices);
                    totalReleased += amounts.released;
                    totalHeld += amounts.held;
                }
                continue;
            }
            const captured = Number(rawCaptured);
            if (captured <= 0)
                continue;
            const split = (0, case_payment_status_helpers_js_1.splitCasePaymentTotals)(captured, Number(invoice.instantReleasePercent));
            totalReleased += split.totalReleased;
            totalHeld += split.totalHeld;
            continue;
        }
        const milestone = milestones[0];
        const onInvoice = (input.adjustments ?? []).filter((item) => item.status === "accepted" && item.invoiceId === invoice.id);
        const additions = onInvoice
            .filter((item) => item.kind === "addition")
            .map((item) => ({ id: item.id, amount: Math.abs(Number(item.amount || 0)), createdAt: item.createdAt }));
        const deductions = onInvoice
            .filter((item) => item.kind === "reduction")
            .map((item) => ({ id: item.id, displayCode: item.displayCode, amount: Math.abs(Number(item.amount || 0)) }));
        const shared = {
            captured: invoice.milestoneAmountCaptured,
            milestoneAmount: Number(milestone.budgetAmount || 0),
            additions,
            deductions,
            releasePercent: Number(invoice.instantReleasePercent),
            paid: true,
        };
        const milestoneDisplay = fundedCasePositionDisplay({
            ...shared,
            target: {
                kind: "milestone",
                fullyReleased: milestone.paymentRequestStatus === "approved",
                drawdownAmount: milestone.drawdownAmount,
            },
        });
        totalReleased += milestoneDisplay?.released ?? 0;
        totalHeld += milestoneDisplay?.held ?? 0;
        for (const addition of additions) {
            const variationDisplay = fundedCasePositionDisplay({
                ...shared,
                target: { kind: "variation", id: addition.id, fullyReleased: input.releasedVariationIds?.has(addition.id) === true },
            });
            totalReleased += variationDisplay?.released ?? 0;
            totalHeld += variationDisplay?.held ?? 0;
        }
    }
    return {
        totalReleased: (0, round_money_js_1.roundMoney)(totalReleased),
        totalHeld: (0, round_money_js_1.roundMoney)(totalHeld),
    };
}
function formatAmount(amount) {
    return (0, round_money_js_1.roundMoney)(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function deductionList(lines, prefix) {
    return lines
        .map((line) => `${prefix ? `${prefix} of ` : ""}${line.displayCode} (${formatAmount(line.amount)})`)
        .join(" and ");
}
/**
 * CASE / Safeguard money columns for one position.
 * Cells are the current released and held amounts. The tooltip carries the deduction formula.
 */
function casePositionEscrowDisplay(input) {
    if (!input.paid) {
        return { released: 0, held: 0, releasedTooltip: null, heldTooltip: null };
    }
    const position = (0, round_money_js_1.roundMoney)(Math.max(0, Number(input.positionAmount) || 0));
    const lines = input.allocatedDeductions.filter((line) => line.amount > 0);
    const deduction = (0, round_money_js_1.roundMoney)(lines.reduce((sum, line) => sum + line.amount, 0));
    const net = (0, round_money_js_1.roundMoney)(Math.max(0, position - deduction));
    const subjectLabel = input.subject === "milestone" ? "Milestone" : "Variation";
    const subjectPhrase = input.subject === "milestone" ? "the milestone" : "the variation";
    if (input.fullyReleased) {
        const tooltip = deduction > 0 ? `${subjectLabel} ${formatAmount(position)} minus ${deductionList(lines)} = ${formatAmount(net)}` : null;
        return { released: net, held: 0, releasedTooltip: tooltip, heldTooltip: null };
    }
    const percent = Number(input.releasePercent ?? 50);
    if (percent >= 100) {
        const tooltip = deduction > 0 ? `${subjectLabel} ${formatAmount(position)} minus ${deductionList(lines)} = ${formatAmount(net)}` : null;
        return { released: net, held: 0, releasedTooltip: tooltip, heldTooltip: null };
    }
    const splitPercent = percent > 0 ? percent : 0;
    const gross = (0, case_payment_status_helpers_js_1.splitCasePaymentTotals)(position, splitPercent <= 0 ? 0 : splitPercent);
    const netSplit = (0, case_payment_status_helpers_js_1.splitCasePaymentTotals)(net, splitPercent <= 0 ? 0 : splitPercent);
    const afterDrawdown = (0, case_payment_status_helpers_js_1.applyCaseDrawdownToEscrowAmounts)(netSplit.totalReleased, netSplit.totalHeld, input.drawdownAmount);
    const halfLabel = splitPercent > 0 ? `${splitPercent}%` : "0%";
    const tooltip = deduction > 0
        ? `${halfLabel} of ${subjectPhrase} (${formatAmount(gross.totalReleased || gross.totalHeld)}) minus ${deductionList(lines, halfLabel)} = ${formatAmount(splitPercent > 0 ? netSplit.totalHeld : net)}`
        : null;
    if (splitPercent <= 0) {
        return {
            released: afterDrawdown.released,
            held: afterDrawdown.held,
            releasedTooltip: tooltip,
            heldTooltip: tooltip,
        };
    }
    return {
        released: afterDrawdown.released,
        held: afterDrawdown.held,
        releasedTooltip: tooltip,
        heldTooltip: tooltip,
    };
}
